import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { useReveal } from "../hooks/useReveal";
import Tilt3D from "./Tilt3D";
import { PlayCircle, Clock, Lock, ArrowRight, Star, GraduationCap, Scissors, Sparkles } from "lucide-react";

function Reveal({ children, delay = 0, className = "" }) {
  const { ref, visible } = useReveal();
  return <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

const STEP_ICONS = [Sparkles, GraduationCap, PlayCircle];
const lessonCount = (c) => (c.modules || []).reduce((n, m) => n + (m.lessons || []).length, 0);

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.getContent().then(setContent).catch(() => {});
    api.listCourses().then(setCourses).catch(() => {});
  }, []);

  const hero = content?.hero || {};
  const stats = content?.stats || [];
  const steps = content?.steps || [];
  const reviews = content?.reviews || [];

  return (
    <main className="relative overflow-hidden">
      <section className="relative pt-[72px]">
        <div className="absolute -top-20 -left-32 h-[420px] w-[420px] rounded-full bg-[#ff3798]/30 blob" />
        <div className="absolute top-40 -right-24 h-[360px] w-[360px] rounded-full bg-[#d6ff71]/15 blob" style={{ animationDelay: "3s" }} />
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24 relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d6ff71]/40 bg-[#d6ff71]/10 px-4 py-1.5 text-xs text-[#d6ff71] animate-fade-up">{hero.badge || "Школа парикмахерского искусства"}</div>
            <h1 className="mt-6 text-5xl lg:text-7xl font-extrabold leading-[1.03] animate-fade-up" style={{ animationDelay: "80ms" }}>
              {hero.titleLine1 || "Онлайн-курсы"}<br />
              <span className="text-gradient-brand">{hero.titleAccent || "по кудрям"}</span> {hero.titleLine2 || "и стилю"}
            </h1>
            <p className="mt-6 text-lg text-white/60 max-w-md animate-fade-up" style={{ animationDelay: "160ms" }}>{hero.subtitle || "Научись создавать идеальные локоны, укладки и окрашивание с нуля."}</p>
            <div className="mt-8 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "240ms" }}>
              <button onClick={() => navigate(user ? "/dashboard" : "/auth?mode=register")} className="group inline-flex items-center gap-2 rounded-full bg-[#ff3798] px-7 py-3.5 font-semibold hover:bg-[#ff5aac] transition-colors shadow-[0_12px_32px_-10px_rgba(255,55,152,0.9)]">Начать обучение <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></button>
              <button onClick={() => document.getElementById("courses")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 rounded-full border border-[#d6ff71]/50 text-[#d6ff71] px-7 py-3.5 font-medium hover:bg-[#d6ff71] hover:text-black transition-colors"><PlayCircle size={18} /> Смотреть курсы</button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-white/50 animate-fade-up" style={{ animationDelay: "320ms" }}>
              {stats.map((s, i) => (<React.Fragment key={i}>{i > 0 && <div className="h-8 w-px bg-white/15" />}<div><span className="text-[#d6ff71] text-2xl font-bold">{s.value}</span><br />{s.label}</div></React.Fragment>))}
            </div>
          </div>
          <div className="perspective">
            <Tilt3D max={10} className="relative">
              <div className="absolute inset-0 -m-4 rounded-[2rem] bg-gradient-to-tr from-[#ff3798]/40 to-[#d6ff71]/20 blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl">
                <img src={hero.image} alt="Кудри" className="w-full h-[520px] object-cover" />
                <div className="tilt-layer absolute bottom-4 left-4 right-4 rounded-2xl bg-black/50 backdrop-blur-md border border-white/10 p-4 flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-[#ff3798] flex items-center justify-center"><Scissors size={20} /></div>
                  <div><div className="text-sm font-semibold">{hero.cardTitle || "Авторская методика"}</div><div className="text-xs text-white/50">{hero.cardSubtitle || "От практикующих мастеров"}</div></div>
                </div>
              </div>
            </Tilt3D>
          </div>
        </div>
      </section>

      <section id="courses" className="max-w-7xl mx-auto px-5 py-20 relative z-10">
        <Reveal><div className="mb-10"><h2 className="text-4xl font-extrabold">Наши <span className="text-gradient-pink">курсы</span></h2><p className="text-white/50 mt-2">Выберите направление, которое вам по душе</p></div></Reveal>
        <div className="grid md:grid-cols-3 gap-7 perspective">
          {courses.map((c, i) => (
            <Reveal key={c.id} delay={i * 120}>
              <div className="group rounded-3xl overflow-hidden bg-white/[0.03] border border-white/10 card-3d">
                <div className="relative h-52 overflow-hidden">
                  <img src={c.cover} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <span className="absolute top-4 left-4 rounded-full bg-[#d6ff71] text-black px-3 py-1 text-xs font-semibold">{c.level}</span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold">{c.title}</h3>
                  <p className="text-sm text-[#ff9ccb] mt-1">{c.subtitle}</p>
                  <p className="text-sm text-white/50 mt-3 line-clamp-2">{c.description}</p>
                  <div className="mt-5 flex items-center justify-between text-sm text-white/50">
                    <span className="flex items-center gap-1.5"><PlayCircle size={16} />{lessonCount(c)} уроков</span>
                    <span className="flex items-center gap-1.5"><Clock size={16} />{c.duration}</span>
                  </div>
                  <button onClick={() => navigate(user ? `/course/${c.id}` : "/auth?mode=register")} className="mt-6 w-full rounded-full border border-[#ff3798]/50 py-2.5 text-sm font-semibold text-[#ff9ccb] hover:bg-[#ff3798] hover:text-white transition-colors flex items-center justify-center gap-2">{user ? "Открыть курс" : <><Lock size={15} /> Получить доступ</>}</button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="how" className="relative py-20 border-y border-white/5 bg-white/[0.015]">
        <div className="max-w-7xl mx-auto px-5">
          <Reveal><h2 className="text-4xl font-extrabold text-center">Почему <span className="text-gradient-brand">HairMama</span></h2></Reveal>
          <div className="grid md:grid-cols-3 gap-7 mt-12 perspective">
            {steps.map((s, i) => { const Icon = STEP_ICONS[i % STEP_ICONS.length]; return (
              <Reveal key={i} delay={i * 130}>
                <div className="relative rounded-3xl bg-white/[0.03] border border-white/10 p-8 card-3d">
                  <div className="absolute -top-4 -right-2 text-7xl font-extrabold text-white/5">{i + 1}</div>
                  <div className="h-14 w-14 rounded-2xl bg-[#ff3798]/15 border border-[#ff3798]/30 flex items-center justify-center text-[#ff3798]"><Icon size={26} /></div>
                  <h3 className="mt-5 text-xl font-bold">{s.title}</h3>
                  <p className="mt-2 text-white/50">{s.text}</p>
                </div>
              </Reveal>); })}
          </div>
        </div>
      </section>

      <section id="reviews" className="max-w-7xl mx-auto px-5 py-20">
        <Reveal><h2 className="text-4xl font-extrabold text-center">Отзывы <span className="text-gradient-pink">учениц</span></h2></Reveal>
        <div className="grid md:grid-cols-3 gap-7 mt-12 perspective">
          {reviews.map((r, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="rounded-3xl bg-white/[0.03] border border-white/10 p-7 card-3d">
                <div className="flex gap-1 text-[#d6ff71] mb-4">{[...Array(5)].map((_, k) => <Star key={k} size={16} fill="currentColor" />)}</div>
                <p className="text-white/80">«{r.text}»</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#ff3798] to-[#d6ff71] flex items-center justify-center font-bold text-black">{r.name?.[0]}</div>
                  <div><div className="font-semibold text-sm">{r.name}</div><div className="text-xs text-white/40">{r.role}</div></div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#ff3798] to-[#d63b86] p-12 md:p-16 text-center">
            <div className="absolute -top-16 -right-10 h-64 w-64 rounded-full bg-[#d6ff71]/20 blur-2xl" />
            <h2 className="relative text-3xl md:text-5xl font-extrabold">Готовы стать мастером?</h2>
            <p className="relative mt-4 text-white/85 max-w-xl mx-auto">Регистрируйтесь сейчас и начните обучение уже сегодня.</p>
            <button onClick={() => navigate(user ? "/dashboard" : "/auth?mode=register")} className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-[#d6ff71] text-black px-8 py-4 font-semibold hover:bg-[#c4f24d] transition-colors">{user ? "Перейти в кабинет" : "Создать аккаунт"} <ArrowRight size={18} /></button>
          </div>
        </Reveal>
      </section>

      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-4 text-white/40 text-sm">
          <div><span className="font-script text-xl text-[#ff3798]">Hair</span><span className="font-script text-xl text-white">Mama</span></div>
          <p>© 2025 HairMama. Школа парикмахерского искусства.</p>
        </div>
      </footer>
    </main>
  );
}
